import type React from 'react'
import { useFormContext } from 'react-hook-form'
import { Button } from '../../../components/ui/Button'
import SelectInput from '../../../components/FormElements/SelectInput'
import { useGetCurrencies } from '../../../api/utils/useGetCurrencies'
import { useEffect, useMemo } from 'react'
import { useGetUser } from '../../../api/auth/useGetUser'

interface UserSettings {
  target_currency: {
    label: string
    value: number
  }
  language: {
    label: string
    value: string
  }
}

interface UserSettingsSectionProps {
  isEditing: boolean
  onCancel: () => void
}

export const UserSettingsSection: React.FC<UserSettingsSectionProps> = ({
  isEditing,
  onCancel,
}) => {
  const { reset } = useFormContext<UserSettings>()
  const { data: currencies } = useGetCurrencies()
  const { data: user } = useGetUser()

  const targetCurrency = useMemo(
    () => currencies?.find((d) => d.code === user?.target_currency_code),
    [currencies, user?.target_currency_code],
  )

  const currencyOptions = useMemo(
    () =>
      currencies?.map((d) => {
        return {
          label: `${d.name} (${d.symbol})`,
          value: d.id,
        }
      }),
    [currencies],
  )

  const languageOptions = useMemo(
    () => [
      {
        label: 'English',
        value: 'en',
      },
      // {
      //   label: 'Spanish',
      //   value: 'es',
      // },
    ],
    [],
  )

  useEffect(() => {
    reset({
      target_currency: currencyOptions?.find((d) => d.value === targetCurrency?.id),
      language: languageOptions?.find((d) => d.value === user?.language),
    })
  }, [currencyOptions, languageOptions, reset, targetCurrency?.id, user?.language])

  if (!isEditing) {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-md font-bold text-foreground dark:text-white">
            Target Currency
          </h3>
          <p className="mt-1 text-sm text-foreground dark:text-white">
            {targetCurrency?.name} ({targetCurrency?.symbol})
          </p>
        </div>
        <div>
          <h3 className="text-md font-bold text-foreground dark:text-white">Language</h3>
          <p className="mt-1 text-sm text-foreground dark:text-white">
            {languageOptions.find((d) => d.value === user?.language)?.label}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <SelectInput
        label="Target Currency"
        name={'target_currency'}
        options={currencyOptions ?? []}
        infoMessage={'The currency you want to calculate gains for (your home currency)'}
      />
      <SelectInput name={'language'} label="Language" options={languageOptions} />

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">Save</Button>
      </div>
    </div>
  )
}
